import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type {
  AddAllowedDomainRequest,
  AllowedEmailDomainResponse,
} from "@/types/generated/cms.generated";
import { errorMessage } from "@/lib/errorMessage";

/**
 * Allowed email domains for an organization, managed by the Athlyn team ON BEHALF of the client.
 * Anyone with an email on one of these exact hosts can register as an employee of the org.
 * CMS API: `GET/POST /organizations/{id}/allowed-domains`, `DELETE /organizations/{id}/allowed-domains/{domain}`.
 * The server owns normalisation, the free-provider blocklist, cross-org uniqueness, the limit and
 * the Entra block — the editor only pre-checks the shape so obvious typos never round-trip.
 */

export const allowedDomainsKey = (organizationId: string | null | undefined) =>
  ["organizations", organizationId, "allowed-domains"] as const;

export function useOrgAllowedDomains(organizationId: string | null | undefined) {
  return useQuery<AllowedEmailDomainResponse[]>({
    queryKey: allowedDomainsKey(organizationId),
    queryFn: () => apiRequest("GET", `/organizations/${organizationId}/allowed-domains`),
    enabled: !!organizationId,
  });
}

export function useAddOrgAllowedDomain(organizationId: string | null | undefined) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    // A refused add (400/409) is a business answer, never worth retrying.
    retry: false,
    mutationFn: (domain: string) => {
      const body: AddAllowedDomainRequest = { domain };
      return apiRequest<AllowedEmailDomainResponse>(
        "POST",
        `/organizations/${organizationId}/allowed-domains`,
        body,
      );
    },
    onSuccess: (added) => {
      queryClient.invalidateQueries({ queryKey: allowedDomainsKey(organizationId) });
      toast({
        title: "Domain added",
        description: `People with an @${added?.domain ?? "domain"} email can now register.`,
      });
    },
    // No toast on error: the editor renders the typed reason inline next to the field.
  });
}

export function useRemoveOrgAllowedDomain(organizationId: string | null | undefined) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    retry: false,
    mutationFn: (domain: string) =>
      apiRequest(
        "DELETE",
        `/organizations/${organizationId}/allowed-domains/${encodeURIComponent(domain)}`,
      ),
    onSuccess: (_data, domain) => {
      queryClient.invalidateQueries({ queryKey: allowedDomainsKey(organizationId) });
      toast({
        title: "Domain removed",
        description: `New sign-ups from @${domain} are no longer accepted.`,
      });
    },
    onError: (error: unknown) => {
      toast({
        title: "Error",
        description: errorMessage(error, "Failed to remove the domain. Please try again."),
        variant: "destructive",
      });
    },
  });
}

const DOMAIN_ERROR_MESSAGES: Record<string, string> = {
  INVALID_DOMAIN: "Enter a valid domain, like company.com — no @, no http://, no path.",
  FREE_EMAIL_PROVIDER_DOMAIN:
    "Free email provider domains (like gmail.com) can't be used — anyone could register.",
  DOMAIN_ALREADY_CLAIMED: "This domain is already used by another organization.",
  DOMAIN_ALREADY_ADDED: "This domain is already on this organization's list.",
  DOMAIN_LIMIT_REACHED:
    "This organization has reached its domain limit. Remove a domain before adding another.",
  DOMAINS_BLOCKED_FOR_ENTRA:
    "This organization signs in with Microsoft Entra, so allowed domains can't be used.",
};

/** The message shown inline for a refused add, by the API's typed error code. */
export function allowedDomainErrorMessage(error: unknown): string {
  const err = error as { errorCode?: string; message?: string } | null | undefined;
  if (err?.errorCode && DOMAIN_ERROR_MESSAGES[err.errorCode]) {
    return DOMAIN_ERROR_MESSAGES[err.errorCode];
  }
  return err?.message || "Could not add the domain. Please try again.";
}

// Exact host: dot-separated labels of letters/digits/hyphens (IDN letters allowed), no leading or
// trailing hyphen per label, at least two labels.
// (Built with the RegExp constructor: tsconfig has no `target`, so a `/u` literal fails type-check.)
const HOST_PATTERN = new RegExp(
  "^(?!-)[\\p{L}\\p{N}-]{1,63}(?<!-)(?:\\.(?!-)[\\p{L}\\p{N}-]{1,63}(?<!-))+$",
  "u",
);

/**
 * Client-side shape check; returns the trimmed, lower-cased host or the refusal message.
 * When the org's loaded list is passed, a host already on it is refused with the same text as the
 * server's `DOMAIN_ALREADY_ADDED` so it never round-trips (the server still enforces it).
 */
export function validateDomainInput(
  raw: string,
  existing?: ReadonlyArray<string | null | undefined>,
): { domain: string } | { error: string } {
  const domain = raw.trim().toLowerCase().replace(/\.$/, "");
  if (!domain || !HOST_PATTERN.test(domain)) {
    return { error: DOMAIN_ERROR_MESSAGES.INVALID_DOMAIN };
  }
  if (existing?.some((d) => (d ?? "").trim().toLowerCase().replace(/\.$/, "") === domain)) {
    return { error: DOMAIN_ERROR_MESSAGES.DOMAIN_ALREADY_ADDED };
  }
  return { domain };
}
